import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import '@fortawesome/fontawesome-free/css/all.min.css';

const EventsList = () => {
  const [events, setEvents] = useState([]);
  const [filterType, setFilterType] = useState('upcoming');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/events`);
      setEvents(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return filterType === 'upcoming' ? eventDate >= today : eventDate < today;
  });

  return (
    <div className="pb-20 sm:pb-32 md:pb-40 bg-[#f5f3f0]">

      {/* Hero Section */}
      <section className="relative w-full h-[400px] sm:h-[500px] md:h-[600px] lg:h-[650px]">
        <img
          src="/images/events.jpg"
          alt="Events"
          className="w-full h-full object-cover brightness-75"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60" />
        
        {/* Hero Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm font-black text-white/90 uppercase tracking-widest mb-3 sm:mb-4">
              Community Gatherings
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase leading-tight mb-4 sm:mb-6">
              Our Events
            </h1>
           
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        
        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center gap-4 sm:gap-6 my-10 sm:my-12 md:my-16"
        >
          <button
            onClick={() => setFilterType('upcoming')}
            className={`px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4 rounded-xl sm:rounded-2xl font-black text-xs sm:text-sm md:text-base uppercase transition-all duration-300 hover:scale-105 shadow-md
              ${filterType === 'upcoming'
                ? 'bg-gray-900 text-white shadow-xl'
                : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
          >
            Upcoming Events
          </button>

          <button
            onClick={() => setFilterType('past')}
            className={`px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4 rounded-xl sm:rounded-2xl font-black text-xs sm:text-sm md:text-base uppercase transition-all duration-300 hover:scale-105 shadow-md
              ${filterType === 'past'
                ? 'bg-gray-900 text-white shadow-xl'
                : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
          >
            Past Events
          </button>
        </motion.div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredEvents.length === 0 ? (
            <div className="col-span-full flex flex-col items-center justify-center min-h-[400px] text-gray-500">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center p-12 bg-white rounded-3xl shadow-lg"
              >
                <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-calendar-times text-4xl text-gray-400" />
                </div>
                <p className="text-xl sm:text-2xl font-black text-gray-700 uppercase mb-2">
                  No Events Found
                </p>
                <p className="text-sm sm:text-base text-gray-500 font-light">
                  {filterType === 'upcoming' 
                    ? 'Check back soon for upcoming events' 
                    : 'No past events to display'}
                </p>
              </motion.div>
            </div>
          ) : (
            filteredEvents.map((event, index) => (
              <motion.div
                key={event._id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
                  
                  {/* Image Section */}
                  <div className="relative h-56 sm:h-64 overflow-hidden">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                    {/* Date Badge */}
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-xl">
                      <p className="text-3xl sm:text-4xl font-black text-gray-900 leading-none text-center">
                        {new Date(event.date).getDate()}
                      </p>
                      <p className="text-[10px] font-black uppercase text-gray-600 text-center mt-1">
                        {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                      </p>
                    </div>

                    {/* Event Type Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-black text-gray-800 uppercase shadow-lg">
                        {filterType === 'upcoming' ? 'Upcoming' : 'Past Event'}
                      </span>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 sm:p-8 flex flex-col flex-grow">
                    
                    {/* Title */}
                    <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-4 leading-tight line-clamp-2 min-h-[3.5rem]">
                      {event.title}
                    </h3>

                    {/* Details */}
                    <div className="space-y-3 mb-6 flex-grow">
                      
                      {/* Full Date */}
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-calendar-alt text-gray-700 text-sm" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider">Date</p>
                          <p className="text-sm font-light text-gray-800">
                            {new Date(event.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-map-marker-alt text-gray-700 text-sm" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider">Location</p>
                          <p className="text-sm font-light text-gray-800 line-clamp-2">
                            {event.address}
                          </p>
                        </div>
                      </div>

                      {/* Contact */}
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-user text-gray-700 text-sm" />
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-gray-500 uppercase tracking-wider">Contact</p>
                          <p className="text-sm font-light text-gray-800">
                            {event.pointOfContact}
                          </p>
                          <a 
                            href={`tel:${event.number}`}
                            className="text-xs font-black text-gray-600 hover:text-gray-900 transition-colors"
                          >
                            {event.number}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* CTA Button */}
                    <button className="w-full py-3 sm:py-3.5 rounded-xl bg-gray-900 text-white text-sm font-black uppercase hover:bg-gray-800 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-xl flex items-center justify-center gap-2 group/btn">
                      <span>View Details</span>
                      <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsList;





// working old 16/12/25

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';

// const EventsList = () => {
//   const [events, setEvents] = useState([]);
//   const [filterType, setFilterType] = useState('upcoming'); 

//   useEffect(() => {
//     fetchEvents();
//   }, []);

//   const fetchEvents = async () => {
//     try {
//       const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/events`);
//       setEvents(res.data);
//     } catch (err) {
//       console.error(err);
//     }
//   };

//   const filteredEvents = events.filter(event => {
//     const eventDate = new Date(event.date);
//     const today = new Date();
//     today.setHours(0, 0, 0, 0); // normalize for comparison
//     return filterType === 'upcoming' ? eventDate >= today : eventDate < today;
//   });

//   return (
//     <div className="pb-[650px] md:pb-80">
//       {/* Hero Section */}
//       <section className="relative w-full h-[700px]">
//         <img
//           src="/images/events.jpg"
//           alt="Mosque"
//           className="w-full h-full object-cover brightness-75"
//         />
//         <div className="absolute inset-0 bg-black opacity-10"></div>
//       </section>

//       {/* Filter Buttons */}
//       <div className="max-w-6xl mx-auto p-6">
//         <div className="flex gap-4 justify-center mb-10 mt-10">
//           <button
//             onClick={() => setFilterType('upcoming')}
//             className={`px-4 w-40 py-3 rounded-2xl ${filterType === 'upcoming' ? 'bg-gray-800 text-white' : 'bg-gray-300'}`}
//           >
//             Upcoming Events
//           </button>
//           <button
//             onClick={() => setFilterType('past')}
//             className={`px-4 w-40 py-2 rounded-2xl ${filterType === 'past' ? 'bg-gray-800 text-white text-xl' : 'bg-gray-300'}`}
//           >
//             Past Events
//           </button>
//         </div>

//         {/* Responsive Side-by-Side Cards */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
//           {filteredEvents.length === 0 ? (
//   <div className="col-span-full flex items-center justify-center min-h-[300px]">
//     <p className="text-xl md:text-2xl font-semibold text-gray-500">
//       No events found
//     </p>
//   </div>
// ) : (

//             filteredEvents.map(event => (
//               <div
//                 key={event._id}
//                 className="flex flex-col  md:flex-row bg-white shadow-xl rounded-lg overflow-hidden hover:scale-[1.02] transition-transform duration-300"
//               >
//                 <div className="md:w-1/2 h-80">
//                   <img
//                     src={event.image}
//                     alt={event.title}
//                     className="w-full h-full object-cover"
//                   />
//                 </div>
//                 <div className="md:w-1/2 p-6 flex flex-col justify-center">
//                   <h2 className="text-2xl font-bold mb-3">{event.title}</h2>
//                   <p className="text-gray-600 mb-2">
//                     <strong>Date:</strong> {new Date(event.date).toLocaleDateString()}
//                   </p>
//                   <p className="text-gray-600 mb-2">
//                     <strong>Contact:</strong> {event.pointOfContact} ({event.number})
//                   </p>
//                   <p className="text-gray-600">
//                     <strong>Address:</strong> {event.address}
//                   </p>
//                 </div>
//               </div>
//             ))
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default EventsList;






